// src/modules/app-views/suppliers/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import appService from 'services/appService';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import EntityRecordTab from 'modules/app-views/companies/pages/Document/tabs/RecordTab';
import { useSearchParams } from 'react-router-dom';
import { parseImportToken, QUERY_PARAM_NAME, removeTokenFromUrl } from 'utils/crossProfileImport';
import DocumentIdentifier from 'components/core/DocumentIdentifier';
import { FileSyncOutlined, ImportOutlined } from '@ant-design/icons';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import EnrichmentTab from 'modules/app-views/companies/pages/Document/tabs/EnrichmentTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SuppliersDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // Get module configuration to inherit entityType settings
  const storedCurrentPage = useZustand('module', 'currentPage');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for cross-profile import token
  const importToken = searchParams.get(QUERY_PARAM_NAME);
  const importData = useMemo(() => parseImportToken(importToken), [importToken]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const locale = appService.getLocale();

  // Get allowed entityTypes from module config (inherited from config.js)
  // Document pages use api.query.filter (JSON string), list pages use pagination.filter
  const configFilter = useMemo(() => {
    // Try pagination.filter first (list pages)
    if (storedCurrentPage?.pagination?.filter) {
      return storedCurrentPage.pagination.filter;
    }
    // Try api.query.filter (document pages - JSON string)
    if (storedCurrentPage?.api?.query?.filter) {
      try {
        return JSON.parse(storedCurrentPage.api.query.filter);
      } catch {
        return null;
      }
    }
    return null;
  }, [storedCurrentPage?.pagination?.filter, storedCurrentPage?.api?.query?.filter]);

  // Get user preferences for entity type configuration
  // Priority: Module preferences > config.js filter
  const storedParameters = useZustand('app', 'parameters');

  const entityTypePrefs = useMemo(() => {
    const savedPrefs = appService.getModulePreferences({ category: 'form' });
    return {
      entityPersonType: savedPrefs?.entityPersonType || 'manual', // 'manual' | 'person' | 'company'
      entityPersonDefault: savedPrefs?.entityPersonDefault || 'company', // default for hybrid mode
    };
  }, [storedParameters]);

  // Use useMemo to prevent creating new array references on every render
  // allowedEntityTypes is derived from user preferences (entityPersonType)
  const allowedEntityTypes = useMemo(() => {
    // If entityPersonType is fixed ('person' or 'company'), only that type is allowed
    if (entityTypePrefs.entityPersonType === 'person') return ['person'];
    if (entityTypePrefs.entityPersonType === 'company') return ['company'];
    // For 'manual' mode, allow both types (from config.js or default)
    return configFilter?.entityType || ['person', 'company'];
  }, [entityTypePrefs.entityPersonType, configFilter?.entityType]);

  const entityProfile = useMemo(
    () => configFilter?.entityProfile || ['supplier'],
    [configFilter?.entityProfile]
  );

  // Determine the default entityType:
  // - If editing existing data, detect from document fields first (more reliable)
  // - For hybrid mode (manual), use entityPersonDefault from preferences
  // - For fixed mode, use the allowed type
  const defaultEntityType = useMemo(() => {
    // If editing existing data, detect from document fields first (more reliable)
    if (data?.cnpj?.value) return 'company';
    if (data?.cpf?.value) return 'person';
    // Fall back to entityType field if available
    if (data?.entityType) return data.entityType;
    // For fixed mode ('person' or 'company'), use that type
    if (entityTypePrefs.entityPersonType === 'person') return 'person';
    if (entityTypePrefs.entityPersonType === 'company') return 'company';
    // For hybrid mode ('manual'), use entityPersonDefault from preferences
    return entityTypePrefs.entityPersonDefault || 'company';
  }, [data?.entityType, data?.cnpj?.value, data?.cpf?.value, entityTypePrefs]);

  // Determine if this is hybrid mode (allows both company and person)
  // Based on user preferences (entityPersonType === 'manual')
  const isHybridMode = entityTypePrefs.entityPersonType === 'manual';

  // Determine entityTypeModel for SimpleEntityForm:
  // - 'company' or 'person' = fixed type, no selector shown
  // - 'manual' = user can choose between company/person (hybrid mode)
  const entityTypeModel = isHybridMode ? 'manual' : entityTypePrefs.entityPersonType;

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: defaultEntityType,
    entityProfile,
  });

  // Track phone/email values for phoneEmail OR logic
  // When one has value, the other is not required
  const [phoneEmailState, setPhoneEmailState] = useState({
    hasPhone: !!data?.phone?.value,
    hasEmail: !!data?.email?.value,
  });

  // Update phoneEmailState when data loads
  useEffect(() => {
    setPhoneEmailState({
      hasPhone: !!data?.phone?.value,
      hasEmail: !!data?.email?.value,
    });
  }, [data?.phone?.value, data?.email?.value]);

  // Update state when defaultEntityType changes (e.g., when data loads)
  useEffect(() => {
    setState(prev => ({
      ...prev,
      entityType: defaultEntityType,
      entityProfile,
    }));
  }, [defaultEntityType, entityProfile]);

  // Build requiredFields dynamically based on entityType and user preferences
  const requiredFields = useMemo(() => {
    const currentEntityType = state.entityType || defaultEntityType;

    // Base mandatory fields (always required, cannot be changed by user)
    const mandatoryFields = ['countryCode', 'name'];

    // Get user-configured required fields from module preferences
    // Now stored directly as array in 'requiredFields' category
    const savedFields = appService.getModulePreferences({ category: 'requiredFields' });
    const userConfiguredFields = Array.isArray(savedFields) ? savedFields : [];

    // Build final required fields array
    const fields = [...mandatoryFields];

    // Contact fields - map to actual form field names
    if (userConfiguredFields.includes('contactName')) fields.push('contactName');

    // Handle contact fields with phoneEmail special logic
    const hasPhoneEmail = userConfiguredFields.includes('phoneEmail');
    const hasPhone = userConfiguredFields.includes('phone');
    const hasEmail = userConfiguredFields.includes('email');

    if (hasPhoneEmail) {
      // If phoneEmail is active, require at least one of them
      // Dynamic logic: when one has value, the other is not required
      // When both are empty, both are required (showing asterisks on both)
      if (!phoneEmailState.hasPhone && !phoneEmailState.hasEmail) {
        // Neither has value - both are required
        fields.push('phone.value', 'email.value');
      } else if (!phoneEmailState.hasPhone) {
        // Email has value, phone doesn't - only phone shows as "optional" (not required)
        // But we keep the OR marker as fallback validation
        fields.push('phone.value|email.value');
      } else if (!phoneEmailState.hasEmail) {
        // Phone has value, email doesn't - only email shows as "optional" (not required)
        // But we keep the OR marker as fallback validation
        fields.push('phone.value|email.value');
      }
      // If both have values, neither needs to be required
    } else {
      // Normal behavior - add individually if configured
      if (hasPhone) fields.push('phone.value');
      if (hasEmail) fields.push('email.value');
    }

    // Entity-specific fields based on current entityType
    if (currentEntityType === 'company') {
      // Company (pessoa jurídica) fields
      if (userConfiguredFields.includes('cnpj')) fields.push('cnpj.value');
      if (userConfiguredFields.includes('fullName')) fields.push('fullName');
      if (userConfiguredFields.includes('foundedAt')) fields.push('foundedAt');
    } else {
      // Person (pessoa física) fields
      if (userConfiguredFields.includes('cpf')) fields.push('cpf.value');
      if (userConfiguredFields.includes('birthdate')) fields.push('birthdate');
    }

    // Address fields - 'address' enables all address validation
    if (userConfiguredFields.includes('address')) {
      fields.push('address.zip', 'address.address1', 'address.number');
    }

    // Non-BR locale requires additional fields
    if (locale !== 'BR') {
      fields.push('taxId', 'address.address1', 'address.region', 'address.city');
    }

    return fields;
  }, [state.entityType, defaultEntityType, locale, storedParameters, phoneEmailState]);

  // Determine initial focus field based on entityType
  const initFocus = useMemo(() => {
    if (locale !== 'BR') return 'name';
    return state.entityType === 'company' ? 'cnpj.value' : 'cpf.value';
  }, [locale, state.entityType]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = {
      ...props,
      data,
      initTab,
      state,
      setState,
      viewMode,
      entityTypeModel,
      isHybridMode,
      allowedEntityTypes,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode, entityTypeModel, isHybridMode, allowedEntityTypes]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const entityId = values?._id || allValues?._id || data?._id;
      const isNewDocument = !_id || _id === 'add';

      // Start with form values
      const dataToSave = { ...values };

      // For new documents (manual registration), explicitly set origin to 0 (manual)
      // This prevents backend from defaulting to origin: 2 (automatic)
      if (isNewDocument) {
        dataToSave._metadata = { ...dataToSave._metadata, origin: 0 };
      }

      // CROSS-PROFILE IMPORT: Add target profile to dataProfile array
      // This ensures the entity now belongs to both the original and target profiles
      if (importData && importData.targetProfile?.length > 0) {
        const currentDataProfile = data?.dataProfile || [];
        const targetProfile = importData.targetProfile;

        // Merge profiles without duplicates
        const mergedProfiles = [...new Set([...currentDataProfile, ...targetProfile])];

        // Only update if there are new profiles to add
        if (mergedProfiles.length > currentDataProfile.length) {
          dataToSave.dataProfile = mergedProfiles;
        }
      }

      // Include contacts array from data to prevent losing contacts added via EntityChannelsCard
      // But sync form field changes (phone/email) with the corresponding contact entries
      let mergedContacts = data?.contacts ? [...data.contacts] : [];

      // Helper to check if a value is empty (null, undefined, empty string, or whitespace only)
      const isEmpty = val =>
        val === null ||
        val === undefined ||
        val === '' ||
        (typeof val === 'string' && val.trim() === '');

      // Sync phone changes from form to contacts array
      const phoneFromForm = allValues?.phone || values?.phone;
      const phoneId = data?.phone?._id;
      const currentPhoneValue = phoneFromForm?.value;
      const hadPhoneBefore = !!data?.phone?.value;

      if (phoneId) {
        if (isEmpty(currentPhoneValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== phoneId);
          dataToSave.phone = null;
        } else if (currentPhoneValue !== data?.phone?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === phoneId ? { ...c, value: currentPhoneValue } : c
          );
        }
      } else if (hadPhoneBefore && isEmpty(currentPhoneValue)) {
        dataToSave.phone = null;
      }

      // Sync email changes from form to contacts array
      const emailFromForm = allValues?.email || values?.email;
      const emailId = data?.email?._id;
      const currentEmailValue = emailFromForm?.value;
      const hadEmailBefore = !!data?.email?.value;

      if (emailId) {
        if (isEmpty(currentEmailValue)) {
          mergedContacts = mergedContacts.filter(c => c._id !== emailId);
          dataToSave.email = null;
        } else if (currentEmailValue !== data?.email?.value) {
          mergedContacts = mergedContacts.map(c =>
            c._id === emailId ? { ...c, value: currentEmailValue } : c
          );
        }
      } else if (hadEmailBefore && isEmpty(currentEmailValue)) {
        dataToSave.email = null;
      }

      // Only include contacts if there were any originally or if we modified them
      if (data?.contacts || mergedContacts.length > 0) {
        dataToSave.contacts = mergedContacts;
      }

      // Handle CPF clearing
      const cpfFromForm = allValues?.cpf || values?.cpf;
      const currentCpfValue = cpfFromForm?.value;

      if (isEmpty(currentCpfValue) && data?.cpf?.value) {
        dataToSave.cpf = null;
      }

      // Handle CNPJ clearing
      const cnpjFromForm = allValues?.cnpj || values?.cnpj;
      const currentCnpjValue = cnpjFromForm?.value;

      if (isEmpty(currentCnpjValue) && data?.cnpj?.value) {
        dataToSave.cnpj = null;
      }

      await upsert({ dataSet: dataToSave, _id: entityId });

      // CROSS-PROFILE IMPORT: Remove import token from URL after successful save
      // This cleans up the URL and prevents re-processing on page refresh
      if (importToken) {
        searchParams.delete(QUERY_PARAM_NAME);
        setSearchParams(searchParams, { replace: true });
      }
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={initFocus}
          additionalValues={{
            entityProfile: 'supplier',
          }}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          // forceViewAfterSave={false}
          initialValues={initialValues}
          data={data}
          onValuesChange={(changed, allValues) => {
            // Update phoneEmailState when phone or email changes
            // This enables dynamic required field behavior for phoneEmail mode
            if ('phone' in changed || 'email' in changed) {
              const phoneValue = allValues?.phone?.value;
              const emailValue = allValues?.email?.value;
              setPhoneEmailState({
                hasPhone: !!phoneValue && phoneValue.trim() !== '',
                hasEmail: !!emailValue && emailValue.trim() !== '',
              });
            }
          }}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />

          {/*<DocumentIdentifier data={data} />*/}
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SuppliersDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data, viewMode } = props;
  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <EntityRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'enrichment',
      icon: <FileSyncOutlined />,
      label: <span>{translateX('additional_info')}</span>,
      hide: false,
      disabled: false,
      children: <EnrichmentTab />,
    },
  ];
}
